/* Dónde está parado el cursor adentro de una fórmula que se está escribiendo.
 *
 * Igna, 05/10/2026: el asistente de fórmulas de FiboSheets
 * (`routes/AsistenteFormula.jsx`) necesita contestar, con cada tecla, tres
 * preguntas sobre el texto y la posición del cursor:
 *
 *   - ¿se está escribiendo el NOMBRE de una función? → el autocompletar
 *     (`palabraEnCursor`);
 *   - ¿adentro de qué función está, y en qué argumento? → la ayuda con la
 *     firma y el argumento resaltado (`funcionEnCursor`);
 *   - ¿va un argumento acá? → tocar una celda inserta su referencia
 *     (`enPosicionDeArgumento`).
 *
 * Es todo puro (texto + posición → respuesta) para probarlo sin pantalla. No
 * usa el tokenizer del motor a propósito: el motor lee fórmulas TERMINADAS y
 * revienta con una a medio escribir («=SUMA(A1;»), que es justo lo que hay
 * mientras se tipea. Acá se recorre carácter por carácter, llevando la cuenta
 * de lo que importa: si se está adentro de un texto "…" (ahí un «;» o un «(»
 * no cuentan), de un nombre de hoja '…', y de los paréntesis abiertos. */

import { refDe } from "./planilla";

/* Lo que puede tener un nombre de función: letras (con ñ y acentos), números,
   el punto de SUMAR.SI y el guion bajo de HUB.PRECIO_GASOIL. */
const RE_NOMBRE = /[\p{L}\p{N}_.]/u;
const RE_LETRA = /\p{L}/u;

/* Después de esto puede arrancar el nombre de una función (y, por lo tanto,
   tiene sentido el autocompletar): el «=» del principio, abrir paréntesis, los
   separadores, los operadores y un espacio. */
const ANTES_DE_NOMBRE = "=(;,+-*/^&<> ";
/* Antes del cursor, lo que pide un argumento: si lo último escrito es uno de
   éstos, tocar una celda pone su referencia. */
const ANTES_DE_ARGUMENTO = "=(;,+-*/^&<>";
/* Y lo que puede venir después del cursor sin que la referencia se pegue con
   algo: el final, cerrar paréntesis, un separador o un operador. */
const DESPUES_DE_ARGUMENTO = ");,+-*/^&=<>";

export const esFormula = (texto) => typeof texto === "string" && texto.startsWith("=");

/* Recorre la fórmula hasta `hasta` (sin incluirlo). Devuelve si ahí se está
   adentro de un texto o de un nombre de hoja entre comillas, y la pila de
   paréntesis abiertos: de cada uno, la función que lo abrió (null si es un
   paréntesis de agrupar, como en =(A1+B1)*2), dónde está y en qué argumento va
   (cuenta los «;» y las «,» de ESE nivel; el motor acepta los dos). */
export function escanear(texto, hasta = texto.length) {
  const s = String(texto || "");
  const fin = Math.max(0, Math.min(hasta, s.length));
  let enTexto = false;
  let enHoja = false;
  const pila = [];
  for (let i = esFormula(s) ? 1 : 0; i < fin; i += 1) {
    const c = s[i];
    if (enTexto) {
      // Las comillas dobles adentro de un texto se escriben "" (como en Excel):
      // eso cierra y vuelve a abrir, y la cuenta sale igual.
      if (c === '"') enTexto = false;
      continue;
    }
    if (enHoja) {
      if (c === "'") enHoja = false;
      continue;
    }
    if (c === '"') enTexto = true;
    else if (c === "'") enHoja = true;
    else if (c === "(") {
      // El nombre pegado antes del paréntesis (puede haber espacios: el motor
      // acepta «SUMA (A1)»).
      let j = i - 1;
      while (j >= 0 && s[j] === " ") j -= 1;
      const finNombre = j + 1;
      while (j >= 0 && RE_NOMBRE.test(s[j])) j -= 1;
      const nombre = s.slice(j + 1, finNombre);
      pila.push({ nombre: nombre && RE_LETRA.test(nombre[0]) ? nombre : null, abre: i, arg: 0 });
    } else if (c === ")") {
      pila.pop();
    } else if ((c === ";" || c === ",") && pila.length) {
      pila[pila.length - 1].arg += 1;
    }
  }
  return { enTexto, enHoja, pila };
}

/* La función más de adentro en la que está el cursor, y el número de argumento
   (0 = el primero). Un paréntesis de agrupar no cuenta: en
   =SUMA((A1+B1);|) se sigue ayudando con SUMA. null si no está en ninguna. */
export function funcionEnCursor(texto, pos) {
  if (!esFormula(texto)) return null;
  const { pila } = escanear(texto, pos);
  for (let k = pila.length - 1; k >= 0; k -= 1) {
    if (pila[k].nombre) return { nombre: pila[k].nombre, arg: pila[k].arg, abre: pila[k].abre };
  }
  return null;
}

/* El pedazo de nombre que se está escribiendo justo antes del cursor, si está
   donde puede ir una función: { desde, hasta, texto }, donde [desde, hasta) es
   el nombre ENTERO (sigue después del cursor si se lo está corrigiendo por la
   mitad), y `texto` lo escrito hasta el cursor. Puede ser vacío ("=|").
   null si no corresponde: adentro de un texto, de un nombre de hoja, pegado a
   un número o a «$», «!» o «:» (eso es una referencia, no una función). */
export function palabraEnCursor(texto, pos) {
  if (!esFormula(texto) || pos < 1 || pos > texto.length) return null;
  const { enTexto, enHoja } = escanear(texto, pos);
  if (enTexto || enHoja) return null;
  let desde = pos;
  while (desde > 1 && RE_NOMBRE.test(texto[desde - 1])) desde -= 1;
  const escrito = texto.slice(desde, pos);
  if (escrito && !RE_LETRA.test(escrito[0])) return null;
  if (!ANTES_DE_NOMBRE.includes(texto[desde - 1])) return null;
  let hasta = pos;
  while (hasta < texto.length && RE_NOMBRE.test(texto[hasta])) hasta += 1;
  // «Enero|!A1»: es el nombre de una hoja.
  if (texto[hasta] === "!") return null;
  return { desde, hasta, texto: escrito };
}

/* ¿Va un argumento en el cursor? Lo de antes (sin contar espacios) tiene que
   pedirlo —el «=», un «(», un separador o un operador— y lo de después no se
   tiene que pegar con lo que se inserte. Con texto elegido (selStart ≠
   selEnd), lo elegido se reemplaza, como en Excel. */
export function enPosicionDeArgumento(texto, selStart, selEnd = selStart) {
  if (!esFormula(texto) || selStart < 1) return false;
  const { enTexto, enHoja } = escanear(texto, selStart);
  if (enTexto || enHoja) return false;
  let i = selStart - 1;
  while (i > 0 && texto[i] === " ") i -= 1;
  if (!ANTES_DE_ARGUMENTO.includes(texto[i])) return false;
  let j = selEnd;
  while (j < texto.length && texto[j] === " ") j += 1;
  return j >= texto.length || DESPUES_DE_ARGUMENTO.includes(texto[j]);
}

/* El nombre de una hoja como va en una fórmula: suelto si es una palabra
   (Enero), entre comillas simples si tiene espacios o cualquier otra cosa
   ('Hoja 2'), con la comilla de adentro doblada ('Lo de Juan''s'). */
export function hojaEnFormula(nombre) {
  const n = String(nombre);
  if (/^[\p{L}_][\p{L}\p{N}_]*$/u.test(n)) return n;
  return `'${n.replace(/'/g, "''")}'`;
}

/* La referencia de una celda o un rango ({ r0, c0, r1, c1 }, en cualquier
   orden de esquinas), con la hoja adelante si se pasa una. */
export function textoDeRef({ r0, c0, r1 = r0, c1 = c0 }, hoja = null) {
  const a = refDe(Math.min(r0, r1), Math.min(c0, c1));
  const b = refDe(Math.max(r0, r1), Math.max(c0, c1));
  const ref = a === b ? a : `${a}:${b}`;
  return hoja == null ? ref : `${hojaEnFormula(hoja)}!${ref}`;
}

/* Reemplaza [desde, hasta) por `nuevo`. Devuelve el texto y dónde queda el
   cursor (al final de lo insertado). */
export function insertar(texto, desde, hasta, nuevo) {
  return { texto: texto.slice(0, desde) + nuevo + texto.slice(hasta), caret: desde + nuevo.length };
}

/* Pone el nombre elegido en el autocompletar en lugar de lo escrito, con el
   paréntesis abierto y el cursor adentro. Las que no piden nada (HOY, PI) van
   cerradas: =HOY()|. Si ya había un «(» después (se corrigió el nombre de una
   función escrita), no se duplica. */
export function insertarFuncion(texto, palabra, nombre, sinArgumentos = false) {
  const { desde, hasta } = palabra;
  if (texto[hasta] === "(") {
    const r = insertar(texto, desde, hasta, nombre);
    return { texto: r.texto, caret: r.caret + 1 };
  }
  if (sinArgumentos) return insertar(texto, desde, hasta, `${nombre}()`);
  return insertar(texto, desde, hasta, `${nombre}(`);
}
